import { useEffect, useState } from 'react';
import type { Contact } from '@schwarz/contract';

import { Modal } from '../../ui/Modal.js';
import { IconCalendar } from '../../ui/icons.js';
import modal from '../../ui/Modal.module.css';

/**
 * The schedule dialog (S06: "captures date/time for one or many contacts").
 *
 * It does not place a call. It records when one should happen, for the calling
 * engine that arrives with P4 to pick up — which is why this belongs to the phase
 * with no telephony in it at all.
 *
 * Three of the mock's fields are absent.
 *
 * "Retry on no-answer" and "Language" are not wired to anything in the mock
 * either, and have nowhere to be stored — `scheduled_calls` carries no retry
 * policy and no language. A control that silently does nothing would be worse
 * than no control.
 *
 * The offer is the third, and it is gone from the whole build rather than just
 * from this dialog — the column has been dropped from every table. It was never
 * surfaced anywhere and every row held the same default, so it was a field three
 * tables copied between each other and nothing read. If the product wants offers
 * later it wants them designed, not resurrected.
 */
export function ScheduleDialog({
  open,
  contacts,
  busy,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  contacts: Contact[];
  busy: boolean;
  onCancel: () => void;
  onConfirm: (input: { scheduledFor: string; timezone: string }) => void;
}): JSX.Element {
  const [date, setDate] = useState(tomorrow);
  const [time, setTime] = useState('10:00');
  const [error, setError] = useState<string | null>(null);

  // Reopening a day later should not still propose yesterday.
  useEffect(() => {
    if (open) {
      setDate(tomorrow());
      setError(null);
    }
  }, [open]);

  function submit(): void {
    const local = new Date(`${date}T${time}`);
    if (Number.isNaN(local.getTime())) {
      setError('That date and time could not be read.');
      return;
    }
    if (local.getTime() <= Date.now()) {
      setError('Pick a time in the future — a past slot would never be dialled.');
      return;
    }

    onConfirm({
      scheduledFor: local.toISOString(),
      // The operator's own zone, not the server's. The wall clock they typed is
      // theirs, so recording which zone it was typed in is what makes it
      // reproducible across a DST shift.
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
  }

  const names = contacts.map((contact) => contact.name);

  return (
    <Modal
      open={open}
      title="Schedule call"
      subtitle="Records when the call should be placed. The calling engine picks it up at that time."
      onClose={onCancel}
      labelledBy="schedule-title"
      footer={
        <>
          <button type="button" className={modal.ghost} onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className={modal.primary} disabled={busy} onClick={submit}>
            <IconCalendar />
            {busy ? 'Scheduling…' : names.length > 1 ? `Schedule ${names.length}` : 'Schedule call'}
          </button>
        </>
      }
    >
      <div className={modal.target}>{describe(names)}</div>

      {/* Native controls, deliberately. They bring the calendar, the clock
          stepper and the phone keyboard with them, which is worth more than
          dictating the format — and the format is not ours to dictate anyway: a
          native date input renders in the browser’s own locale (Chromium follows
          its UI language, Firefox the OS regional settings) and ignores the
          document’s `lang`. An operator who wants 27.12.2026 gets it by running a
          Bulgarian browser, which is also how every other site will look to them. */}
      <div className={modal.fieldRow}>
        <div className={modal.field}>
          <label htmlFor="schedule-date">Date</label>
          <input
            id="schedule-date"
            type="date"
            value={date}
            min={today()}
            onChange={(event) => setDate(event.target.value)}
          />
        </div>
        <div className={modal.field}>
          <label htmlFor="schedule-time">Time</label>
          <input
            id="schedule-time"
            type="time"
            value={time}
            onChange={(event) => setTime(event.target.value)}
          />
        </div>
      </div>

      {error && <p className={modal.error}>{error}</p>}
    </Modal>
  );
}

/** Names the target, so a bulk schedule cannot be confirmed blind. */
function describe(names: string[]): JSX.Element | string {
  if (names.length === 0) return 'No contacts selected';
  if (names.length === 1) {
    return (
      <>
        Scheduling for <b>{names[0]}</b>
      </>
    );
  }
  const shown = names.slice(0, 2).join(', ');
  const more = names.length > 2 ? ` +${names.length - 2} more` : '';
  return (
    <>
      Scheduling for <b>{names.length} contacts</b> · {shown}
      {more}
    </>
  );
}

/** `yyyy-mm-dd` in the operator's own zone, which is what a date input wants. */
function isoDate(date: Date): string {
  const offsetMs = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 10);
}

function today(): string {
  return isoDate(new Date());
}

function tomorrow(): string {
  return isoDate(new Date(Date.now() + 86_400_000));
}
